<template>
    <div ref="root" class="adt-root">
        <div class="adt-toolbar">
            <div class="adt-legend">
                <button
                    v-for="t in legendTypes"
                    :key="t.key"
                    type="button"
                    :class="['adt-chip', { 'adt-chip-off': hiddenTypes[t.key] }]"
                    :title="t.name"
                    @click="toggleType(t.key)"
                >
                    <span class="adt-swatch" :style="{ background: t.color }"></span>
                    <span class="adt-chip-name">{{ t.name }}</span>
                    <span class="adt-chip-count">{{ t.count }}</span>
                </button>
            </div>
            <label class="adt-only-active">
                <input type="checkbox" v-model="onlyActive" />
                {{ $t('TimelineOnlyActive') }}
            </label>
        </div>

        <div class="adt-zoom">
            <span class="adt-range">{{ rangeLabel }}</span>
            <template v-if="isZoomed">
                <button type="button" class="adt-btn" @click="pan(-1)">&#9664; {{ $t('TimelineEarlier') }}</button>
                <button type="button" class="adt-btn" @click="pan(1)">{{ $t('TimelineLater') }} &#9654;</button>
                <button type="button" class="adt-btn" @click="zoomOut">{{ $t('TimelineZoomOut') }}</button>
                <button type="button" class="adt-btn" @click="resetZoom">{{ $t('TimelineWholeDay') }}</button>
            </template>
            <span v-else class="adt-hint">{{ $t('TimelineZoomHint') }}</span>
        </div>

        <svg class="adt-axis" :width="width" :height="axisHeight">
            <g v-for="tick in ticks" :key="'t' + tick.t">
                <line :x1="tick.x" :x2="tick.x" :y1="axisHeight - 6" :y2="axisHeight" class="adt-tick" />
                <text :x="tick.x" :y="axisHeight - 10" text-anchor="middle" class="adt-tick-label">{{ tick.label }}</text>
            </g>
            <g v-if="cursor != null">
                <rect :x="cursor.x - 28" :y="0" width="56" :height="axisHeight - 6" rx="3" class="adt-cursor-label-bg" />
                <text :x="cursor.x" :y="axisHeight - 12" text-anchor="middle" class="adt-cursor-label">{{ cursor.label }}</text>
            </g>
        </svg>

        <div v-if="rows.length == 0" class="adt-empty">{{ $t('TimelineNoAlarms') }}</div>

        <div v-else class="adt-body" :style="{ maxHeight: maxBodyHeight }">
            <svg
                ref="body"
                :width="width"
                :height="bodyHeight"
                class="adt-body-svg"
                @mousedown="startDrag"
                @mousemove="moveCursor"
                @mouseleave="leavePlot"
            >
                <!-- Tory wierszy i podpisy -->
                <g v-for="row in rows" :key="row.key">
                    <rect :x="labelWidth" :y="row.y" :width="plotWidth" :height="row.height" class="adt-track" />
                    <text :x="labelWidth - 14" :y="row.y + row.height / 2 + 4" text-anchor="end" class="adt-row-label">{{ row.label }}</text>
                </g>

                <!-- Siatka godzin -->
                <line
                    v-for="tick in ticks"
                    :key="'g' + tick.t"
                    :x1="tick.x" :x2="tick.x" :y1="0" :y2="bodyHeight"
                    class="adt-grid"
                />

                <!-- Przyszłość: w wierszu dnia dzisiejszego od „teraz” do końca doby -->
                <rect
                    v-for="row in nowRows"
                    :key="'f' + row.key"
                    :x="row.futureX" :y="row.y" :width="labelWidth + plotWidth - row.futureX" :height="row.height"
                    class="adt-future"
                />

                <!-- Alarmy -->
                <g v-for="bar in bars" :key="bar.key">
                    <rect
                        :x="bar.x" :y="bar.y" :width="bar.w" :height="barHeight" rx="2"
                        :class="['adt-bar', { 'adt-bar-selected': selectedId != null && bar.id == selectedId }]"
                        :style="{ fill: bar.color }"
                        @mouseenter="(e) => showTooltip(e, bar)"
                        @mousemove="(e) => showTooltip(e, bar)"
                        @mouseleave="hideTooltip"
                        @mousedown.stop
                        @click.stop="$emit('select', bar.id)"
                    />
                    <text
                        v-if="bar.continuesLeft"
                        :x="bar.x - 1" :y="bar.y + barHeight - 2"
                        text-anchor="end"
                        class="adt-continues"
                    >&#9664;</text>
                    <text
                        v-if="bar.continuesRight"
                        :x="bar.x + bar.w + 2" :y="bar.y + barHeight - 2"
                        class="adt-continues"
                    >&#9654;</text>
                </g>

                <!-- Teraz -->
                <line
                    v-for="row in nowLines"
                    :key="'n' + row.key"
                    :x1="row.nowX" :x2="row.nowX" :y1="row.y" :y2="row.y + row.height + 2"
                    class="adt-now"
                />

                <!-- Kursor -->
                <line
                    v-if="cursor != null"
                    :x1="cursor.x" :x2="cursor.x" :y1="0" :y2="bodyHeight"
                    class="adt-cursor"
                />

                <!-- Zaznaczanie zakresu do przybliżenia -->
                <rect
                    v-if="drag != null && Math.abs(drag.x2 - drag.x1) > 2"
                    :x="Math.min(drag.x1, drag.x2)" :y="0"
                    :width="Math.abs(drag.x2 - drag.x1)" :height="bodyHeight"
                    class="adt-selection"
                />
            </svg>
        </div>

        <div v-if="nowLines.length > 0" class="adt-now-legend">
            <span class="adt-now-swatch"></span>{{ $t('Now') }} {{ formatClock(now, false) }}
        </div>

        <div
            v-if="tooltip != null"
            class="adt-tooltip"
            :style="{ left: tooltip.left + 'px', top: tooltip.top + 'px' }"
        >
            <div class="adt-tooltip-title">
                <span class="adt-swatch" :style="{ background: tooltip.bar.color }"></span>
                {{ tooltip.bar.typeName }}
            </div>
            <div v-if="tooltip.bar.description" class="adt-tooltip-description">{{ tooltip.bar.description }}</div>
            <div v-if="rowsBy == 'device'" class="adt-tooltip-row">{{ tooltip.bar.deviceLabel }}<template v-if="tooltip.bar.animal"> · {{ tooltip.bar.animal }}</template></div>
            <div class="adt-tooltip-row">
                {{ formatMoment(tooltip.bar.start, tooltip.bar.withDate) }}
                <template v-if="tooltip.bar.isActive"> – {{ $t('TimelineOngoing') }}</template>
                <template v-else-if="tooltip.bar.end > tooltip.bar.start"> – {{ formatMoment(tooltip.bar.end, tooltip.bar.withDate) }}</template>
            </div>
            <div v-if="tooltip.bar.end > tooltip.bar.start" class="adt-tooltip-muted">{{ formatDuration(tooltip.bar.end - tooltip.bar.start) }}</div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'

// Kolejność kolorów jest stała i przypisana do typu alarmu (po jego id), a nie do
// pozycji w legendzie — wyłączenie typu filtrem nie przemalowuje pozostałych.
// Paleta sprawdzona walidatorem kontrastu i daltonizmu dla obu motywów (tło #ffffff / #172124).
const SERIES_COUNT = 8

const MINUTE = 60 * 1000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
const TICK_STEPS = [MINUTE, 2 * MINUTE, 5 * MINUTE, 10 * MINUTE, 15 * MINUTE, 30 * MINUTE, HOUR, 2 * HOUR, 3 * HOUR, 6 * HOUR]
const MIN_RANGE = 5 * MINUTE
const MIN_BAR_WIDTH = 3

const LOCALES = { Polish: 'pl', English: 'en', Norwegian: 'nb' }

const pad = n => String(n).padStart(2, '0')

// Oś X to zawsze godzina doby (0–24 h). Tryby wierszy:
//  - 'device': jeden dzień (prop day), wiersz na urządzenie / „System” / „Zwierzęta”;
//  - 'day':    zakres dni (rangeFrom–rangeTo), wiersz na dzień. Alarm przechodzący przez
//              północ jest dzielony na odcinki w kolejnych wierszach (◀ ▶ na granicy).
export default {
    name: 'AlarmDayTimeline',
    props: {
        // createDate/endDate jako Date albo tekst; endDate null = bez końca.
        alarms: { type: Array, required: true },
        rowsBy: { type: String, default: 'device' },
        // Tryb 'device': ile dni temu (0 = dzisiaj).
        day: { type: Number, default: 0 },
        // Tryb 'day': zakres dni (włącznie).
        rangeFrom: { default: null },
        rangeTo: { default: null },
        selectedId: { default: null },
        maxBodyHeight: { type: String, default: '40vh' }
    },
    data() {
        return {
            width: 800,
            labelWidth: 150,
            axisHeight: 26,
            barHeight: 12,
            laneGap: 4,
            rowPadding: 6,
            viewFrom: null,
            viewTo: null,
            hiddenTypes: {},
            onlyActive: false,
            now: Date.now(),
            tooltip: null,
            cursor: null,
            drag: null
        }
    },
    computed: {
        locale() {
            return LOCALES[this.$store.getters.language] || 'en'
        },
        // Północ każdego pokazywanego dnia.
        dayStarts() {
            if (this.rowsBy === 'day') {
                const first = moment(this.rangeFrom || Date.now()).startOf('day')
                const last = moment(this.rangeTo || Date.now()).startOf('day')
                const days = []
                for (const d = first.clone(); !d.isAfter(last); d.add(1, 'days')) days.push(d.valueOf())
                return days
            }
            return [moment().startOf('day').subtract(this.day, 'days').valueOf()]
        },
        from() {
            return this.viewFrom != null ? this.viewFrom : 0
        },
        to() {
            return this.viewTo != null ? this.viewTo : DAY
        },
        isZoomed() {
            return this.from > 0 || this.to < DAY
        },
        plotWidth() {
            return Math.max(100, this.width - this.labelWidth - 16)
        },
        rangeLabel() {
            const first = this.dayStarts[0]
            const last = this.dayStarts[this.dayStarts.length - 1]
            const dates = first === last
                ? moment(first).format('DD.MM.YYYY')
                : `${moment(first).format('DD.MM')} – ${moment(last).format('DD.MM.YYYY')}`
            if (!this.isZoomed) return dates
            return `${dates}  ${this.formatOffset(this.from)} – ${this.formatOffset(this.to)}`
        },
        intervals() {
            return this.alarms.map((a, index) => {
                const start = new Date(a.createDate).getTime()
                // Brak końca (null) bez IsActive to zdarzenie bez czasu trwania.
                const rawEnd = a.endDate != null ? new Date(a.endDate) : null
                const hasEnd = rawEnd != null && !isNaN(rawEnd.getTime()) && rawEnd.getTime() >= start
                const end = a.isActive ? Math.max(start, this.now) : (hasEnd ? rawEnd.getTime() : start)
                const typeKey = a.alarmTypeId != null ? String(a.alarmTypeId) : String(a.alarmType)
                const isAnimal = a.deviceId == null && typeof a.type === 'string' && a.type.indexOf('Animal') === 0

                return {
                    id: a.id != null ? a.id : 'i' + index,
                    start,
                    end,
                    isActive: !!a.isActive,
                    typeKey,
                    typeName: a.alarmType,
                    // Opcjonalny opis konkretnego alarmu (modale jednego urządzenia).
                    description: a.description || null,
                    deviceKey: a.deviceId != null ? 'd:' + a.deviceId : (isAnimal ? 'animals' : 'system'),
                    deviceLabel: a.deviceId != null ? `${a.type || ''} ${a.number}`.trim() : this.$t(isAnimal ? 'Animals' : 'System'),
                    deviceOrder: a.deviceId != null ? [2, String(a.type || ''), a.number] : [isAnimal ? 1 : 0, '', 0],
                    animal: isAnimal ? a.type.replace('Animal:', '').trim() : null,
                    withDate: this.rowsBy === 'day' || !moment(start).isSame(end, 'day')
                }
            })
        },
        types() {
            const byKey = {}
            this.intervals.forEach(i => {
                if (byKey[i.typeKey] == null) byKey[i.typeKey] = { key: i.typeKey, name: i.typeName, count: 0 }
                byKey[i.typeKey].count++
            })
            const list = Object.keys(byKey).map(k => byKey[k])
            list.sort((a, b) => this.compareIds(a.key, b.key))
            // Ponad 8 typów: reszta dostaje neutralny szary zamiast wymyślonego koloru.
            list.forEach((t, i) => {
                t.color = i < SERIES_COUNT ? `var(--adt-s${i + 1})` : 'var(--adt-other)'
            })
            return byKey
        },
        legendTypes() {
            const list = Object.keys(this.types).map(k => this.types[k])
            return list.sort((a, b) => b.count - a.count || String(a.name).localeCompare(String(b.name)))
        },
        visibleIntervals() {
            return this.intervals.filter(i => !this.hiddenTypes[i.typeKey] && (!this.onlyActive || i.isActive))
        },
        // Odcinki alarmów w obrębie jednej doby, z wierszem, do którego należą.
        segments() {
            const segments = []
            this.visibleIntervals.forEach(i => {
                this.dayStarts.forEach(ds => {
                    const de = ds + DAY
                    const inside = i.end > i.start ? (i.start < de && i.end > ds) : (i.start >= ds && i.start < de)
                    if (!inside) return
                    const isDayRow = this.rowsBy === 'day'
                    segments.push({
                        ...i,
                        dayStart: ds,
                        segStart: Math.max(i.start, ds) - ds,
                        segEnd: Math.min(i.end, de) - ds,
                        continuesLeft: i.start < ds,
                        continuesRight: i.end > de || (i.isActive && i.end <= de),
                        rowKey: isDayRow ? 'day:' + ds : i.deviceKey,
                        rowLabel: isDayRow ? this.dayLabel(ds) : i.deviceLabel,
                        rowOrder: isDayRow ? [ds, '', 0] : i.deviceOrder
                    })
                })
            })
            return segments
        },
        rows() {
            const byKey = {}
            const addRow = (key, label, order, dayStart) => {
                if (byKey[key] == null) byKey[key] = { key, label, order, dayStart, items: [] }
                return byKey[key]
            }
            // Dni bez alarmów też są wierszami — pusty dzień to też informacja.
            if (this.rowsBy === 'day') {
                this.dayStarts.forEach(ds => addRow('day:' + ds, this.dayLabel(ds), [ds, '', 0], ds))
            }
            this.segments.forEach(s => addRow(s.rowKey, s.rowLabel, s.rowOrder, s.dayStart).items.push(s))

            const rows = Object.keys(byKey).map(k => byKey[k])
            rows.sort((a, b) => a.order[0] - b.order[0] || a.order[1].localeCompare(b.order[1]) || a.order[2] - b.order[2])

            // Nakładające się alarmy w jednym wierszu idą na osobne pasy, żeby się nie zasłaniały.
            let y = 0
            rows.forEach(row => {
                const laneEnds = []
                row.items.sort((a, b) => a.segStart - b.segStart)
                row.lanes = row.items.map(item => {
                    let lane = laneEnds.findIndex(end => end < item.segStart)
                    if (lane === -1) {
                        lane = laneEnds.length
                        laneEnds.push(item.segEnd)
                    } else {
                        laneEnds[lane] = item.segEnd
                    }
                    return lane
                })
                const laneCount = Math.max(1, laneEnds.length)
                row.y = y
                row.height = this.rowPadding * 2 + laneCount * this.barHeight + (laneCount - 1) * this.laneGap
                y += row.height + 2
            })
            return rows
        },
        bodyHeight() {
            const last = this.rows[this.rows.length - 1]
            return last ? last.y + last.height : 0
        },
        bars() {
            const bars = []
            this.rows.forEach(row => {
                row.items.forEach((item, idx) => {
                    if (item.segEnd < this.from || item.segStart > this.to) return
                    const x1 = this.x(Math.max(item.segStart, this.from))
                    const x2 = this.x(Math.min(item.segEnd, this.to))
                    bars.push({
                        ...item,
                        key: item.id + '|' + row.key,
                        x: x1,
                        w: Math.max(MIN_BAR_WIDTH, x2 - x1),
                        y: row.y + this.rowPadding + row.lanes[idx] * (this.barHeight + this.laneGap),
                        color: this.types[item.typeKey].color,
                        continuesLeft: item.continuesLeft || item.segStart < this.from,
                        continuesRight: item.continuesRight || item.segEnd > this.to
                    })
                })
            })
            return bars
        },
        // Wiersze, w których dobie jest „teraz”: linia i wyszarzona przyszłość.
        nowRows() {
            return this.rows
                .filter(row => this.now - row.dayStart < DAY && this.now - row.dayStart >= 0 && this.now - row.dayStart < this.to)
                .map(row => {
                    const offset = this.now - row.dayStart
                    return {
                        key: row.key,
                        y: row.y,
                        height: row.height,
                        nowX: offset >= this.from ? this.x(offset) : null,
                        futureX: this.x(Math.max(offset, this.from))
                    }
                })
        },
        nowLines() {
            return this.nowRows.filter(row => row.nowX != null)
        },
        ticks() {
            const range = this.to - this.from
            const maxTicks = Math.max(2, Math.floor(this.plotWidth / 70))
            const step = TICK_STEPS.find(s => range / s <= maxTicks) || TICK_STEPS[TICK_STEPS.length - 1]
            const ticks = []
            for (let t = Math.ceil(this.from / step) * step; t <= this.to; t += step) {
                ticks.push({ t, x: this.x(t), label: this.formatOffset(t) })
            }
            return ticks
        }
    },
    watch: {
        visibleIntervals: {
            immediate: true,
            handler(list) {
                // Tabela pod osią czasu pokazuje to samo co oś (ten sam filtr typów).
                const ids = {}
                list.forEach(i => { ids[i.id] = true })
                this.$emit('visible-change', this.alarms.filter((a, index) => ids[a.id != null ? a.id : 'i' + index]))
            }
        },
        day() {
            this.resetZoom()
        }
    },
    methods: {
        x(offset) {
            return this.labelWidth + (offset - this.from) / (this.to - this.from) * this.plotWidth
        },
        offsetAt(px) {
            const clamped = Math.min(Math.max(px, this.labelWidth), this.labelWidth + this.plotWidth)
            return this.from + (clamped - this.labelWidth) / this.plotWidth * (this.to - this.from)
        },
        compareIds(a, b) {
            const na = parseInt(a, 10)
            const nb = parseInt(b, 10)
            if (!isNaN(na) && !isNaN(nb) && na !== nb) return na - nb
            return a.localeCompare(b)
        },
        dayLabel(ds) {
            const d = new Date(ds)
            return `${d.toLocaleDateString(this.locale, { weekday: 'short' })} ${moment(ds).format('DD.MM')}`
        },
        // Godzina doby z przesunięcia od północy; koniec doby to 24:00, nie 00:00.
        formatOffset(offset, withSeconds) {
            const total = Math.round(offset / 1000)
            const h = Math.floor(total / 3600)
            const m = Math.floor(total % 3600 / 60)
            const s = total % 60
            return withSeconds ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}`
        },
        formatClock(t, withSeconds) {
            return moment(t).format(withSeconds ? 'HH:mm:ss' : 'HH:mm')
        },
        formatMoment(t, withDate) {
            return moment(t).format(withDate ? 'DD.MM HH:mm:ss' : 'HH:mm:ss')
        },
        formatDuration(ms) {
            const minutes = Math.floor(ms / MINUTE)
            if (minutes < 1) return this.$t('TimelineLessThanMinute')
            const d = Math.floor(minutes / (24 * 60))
            const h = Math.floor(minutes % (24 * 60) / 60)
            const m = minutes % 60
            if (d > 0) return `${d} d ${h} h ${m} min`
            return h > 0 ? `${h} h ${m} min` : `${m} min`
        },
        toggleType(key) {
            this.$set(this.hiddenTypes, key, !this.hiddenTypes[key])
        },
        setView(from, to) {
            const range = Math.min(DAY, Math.max(MIN_RANGE, to - from))
            const f = Math.max(0, Math.min(from, DAY - range))
            this.viewFrom = f
            this.viewTo = f + range
        },
        resetZoom() {
            this.viewFrom = null
            this.viewTo = null
        },
        zoomOut() {
            const center = (this.from + this.to) / 2
            const range = (this.to - this.from) * 2
            this.setView(center - range / 2, center + range / 2)
        },
        pan(direction) {
            const shift = (this.to - this.from) / 2 * direction
            this.setView(this.from + shift, this.to + shift)
        },
        localX(event) {
            const rect = this.$refs.body.getBoundingClientRect()
            return event.clientX - rect.left
        },
        startDrag(event) {
            if (event.button !== 0) return
            const px = this.localX(event)
            if (px < this.labelWidth) return
            this.drag = { x1: px, x2: px }
            window.addEventListener('mousemove', this.onDragMove)
            window.addEventListener('mouseup', this.endDrag)
        },
        onDragMove(event) {
            if (this.drag == null) return
            const px = Math.min(Math.max(this.localX(event), this.labelWidth), this.labelWidth + this.plotWidth)
            this.drag = { x1: this.drag.x1, x2: px }
        },
        endDrag() {
            window.removeEventListener('mousemove', this.onDragMove)
            window.removeEventListener('mouseup', this.endDrag)
            const drag = this.drag
            this.drag = null
            if (drag == null || Math.abs(drag.x2 - drag.x1) < 6) return
            // Zaokrąglenie do okrągłych godzin, żeby zakres nie wyglądał na przypadkowy (04:59 – 08:02).
            const rawFrom = this.offsetAt(Math.min(drag.x1, drag.x2))
            const rawTo = this.offsetAt(Math.max(drag.x1, drag.x2))
            const span = rawTo - rawFrom
            const snap = span > 2 * HOUR ? 15 * MINUTE : span > 30 * MINUTE ? 5 * MINUTE : MINUTE
            this.setView(Math.round(rawFrom / snap) * snap, Math.round(rawTo / snap) * snap)
        },
        moveCursor(event) {
            const px = this.localX(event)
            if (px < this.labelWidth || px > this.labelWidth + this.plotWidth) {
                this.cursor = null
                return
            }
            this.cursor = { x: px, label: this.formatOffset(this.offsetAt(px), this.to - this.from <= 2 * HOUR) }
        },
        leavePlot() {
            this.cursor = null
            this.hideTooltip()
        },
        showTooltip(event, bar) {
            const rootRect = this.$refs.root.getBoundingClientRect()
            let left = event.clientX - rootRect.left + 14
            const top = event.clientY - rootRect.top + 14
            if (left + 260 > rootRect.width) left = event.clientX - rootRect.left - 274
            this.tooltip = { bar, left: Math.max(0, left), top }
        },
        hideTooltip() {
            this.tooltip = null
        },
        measure() {
            const root = this.$refs.root
            if (root == null) return
            // clientWidth liczy też padding, a SVG stoi wewnątrz niego.
            const style = window.getComputedStyle(root)
            this.width = root.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
        }
    },
    mounted() {
        this.measure()
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.measure())
            this.resizeObserver.observe(this.$refs.root)
        } else {
            window.addEventListener('resize', this.measure)
        }
        // Trwające alarmy i linia „teraz” przesuwają się same, bez przeładowania.
        this.clock = setInterval(() => { this.now = Date.now() }, 30 * 1000)
    },
    beforeDestroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect()
        window.removeEventListener('resize', this.measure)
        window.removeEventListener('mousemove', this.onDragMove)
        window.removeEventListener('mouseup', this.endDrag)
        clearInterval(this.clock)
    }
}
</script>

<style scoped>
.adt-root {
    --adt-s1: #2a78d6;
    --adt-s2: #eb6834;
    --adt-s3: #1baf7a;
    --adt-s4: #eda100;
    --adt-s5: #e87ba4;
    --adt-s6: #008300;
    --adt-s7: #4a3aa7;
    --adt-s8: #e34948;
    --adt-other: #8a8f90;
    --adt-now: #d64545;

    position: relative;
    padding: 8px 12px 4px;
    color: var(--text);
    background: var(--surface);
    user-select: none;
}

html[data-theme="dark"] .adt-root {
    --adt-s1: #3987e5;
    --adt-s2: #d95926;
    --adt-s3: #199e70;
    --adt-s4: #c98500;
    --adt-s5: #d55181;
    --adt-s6: #008300;
    --adt-s7: #9085e9;
    --adt-s8: #e66767;
    --adt-other: #7d8c8f;
    --adt-now: #e66767;
}

.adt-toolbar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.adt-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.adt-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 280px;
    padding: 3px 10px;
    border: 1px solid var(--border);
    border-radius: 14px;
    background: var(--surface);
    color: var(--text-strong);
    font-size: 12px;
    cursor: pointer;
}

.adt-chip:hover {
    background: var(--surface-hover);
}

.adt-chip-off {
    color: var(--text-muted);
    text-decoration: line-through;
}

.adt-chip-off .adt-swatch {
    opacity: 0.25;
}

.adt-chip-name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.adt-chip-count {
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
}

.adt-swatch {
    display: inline-block;
    flex: none;
    width: 10px;
    height: 10px;
    border-radius: 2px;
}

.adt-only-active {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 4px 0 0;
    white-space: nowrap;
    font-size: 12px;
    color: var(--text);
    cursor: pointer;
}

.adt-zoom {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin: 8px 0 2px;
    font-size: 12px;
}

.adt-range {
    font-weight: 600;
    color: var(--text-strong);
    white-space: pre;
}

.adt-hint {
    color: var(--text-muted);
}

.adt-btn {
    padding: 2px 8px;
    border: 1px solid var(--border);
    border-radius: 4px;
    background: var(--surface);
    color: var(--text);
    font-size: 12px;
    cursor: pointer;
}

.adt-btn:hover {
    background: var(--surface-hover);
}

.adt-axis {
    display: block;
}

.adt-tick {
    stroke: var(--text-muted);
}

.adt-tick-label {
    fill: var(--text-muted);
    font-size: 11px;
    font-variant-numeric: tabular-nums;
}

.adt-cursor-label-bg {
    fill: var(--text-strong);
}

.adt-cursor-label {
    fill: var(--surface);
    font-size: 11px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.adt-body {
    overflow-y: auto;
    overflow-x: hidden;
}

.adt-body-svg {
    display: block;
    cursor: crosshair;
}

.adt-track {
    fill: var(--surface-muted);
}

.adt-row-label {
    fill: var(--text-strong);
    font-size: 12px;
}

.adt-grid {
    stroke: var(--border);
    stroke-width: 1;
}

.adt-future {
    fill: var(--surface-hover);
}

.adt-bar {
    stroke: var(--surface);
    stroke-width: 1;
    cursor: pointer;
}

.adt-bar:hover {
    stroke: var(--text-strong);
}

.adt-bar-selected {
    stroke: var(--text-strong);
    stroke-width: 2;
}

.adt-continues {
    fill: var(--text-muted);
    font-size: 9px;
    pointer-events: none;
}

.adt-now {
    stroke: var(--adt-now);
    stroke-width: 2;
    pointer-events: none;
}

.adt-cursor {
    stroke: var(--text-muted);
    stroke-dasharray: 3 3;
    pointer-events: none;
}

.adt-selection {
    fill: var(--accent);
    fill-opacity: 0.15;
    stroke: var(--accent);
    pointer-events: none;
}

.adt-now-legend {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    font-size: 11px;
    color: var(--text-muted);
}

.adt-now-swatch {
    display: inline-block;
    width: 2px;
    height: 12px;
    background: var(--adt-now);
}

.adt-empty {
    padding: 24px 0;
    text-align: center;
    color: var(--text-muted);
    font-size: 13px;
}

.adt-tooltip {
    position: absolute;
    z-index: 5;
    width: 260px;
    padding: 8px 10px;
    border: 1px solid var(--popover-border);
    border-radius: 6px;
    background: var(--popover-bg);
    color: var(--popover-fg);
    box-shadow: var(--popover-shadow);
    font-size: 12px;
    pointer-events: none;
}

.adt-tooltip-title {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
    font-weight: 600;
}

.adt-tooltip-description {
    margin-bottom: 4px;
    word-break: break-word;
}

.adt-tooltip-row {
    font-variant-numeric: tabular-nums;
}

.adt-tooltip-muted {
    color: var(--popover-muted);
}
</style>



// WEBPACK FOOTER //
// src/components/Charts/AlarmDayTimeline.vue